import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { DatePickerComponent } from '@shared/components/date-picker/date-picker.component';
import { OptionalFieldComponent } from '@shared/components/optional-field/optional-field.component';
import { UnitSelectorComponent } from '@shared/components/unit-selector/unit-selector.component';
import { NotificationService } from '@shared/services/notification.service';
import { OrganizationUnitItem, ReferenceItem } from '@features/user/models/filter-data.model';
import { Person } from '../../models/person.model';
import { PersonEvent } from '../../models/person-event.model';
import { PersonService } from '../../services/person.service';
import { getPersonEventEditorErrorMessage } from '../../pages/person-event-editor-page/person-event-editor.utils';

type ActPlaceMode = 'none' | 'community' | 'location';

@Component({
  selector: 'app-person-appointment-editor-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatRadioModule,
    MatSelectModule,
    DatePickerComponent,
    OptionalFieldComponent,
    UnitSelectorComponent,
  ],
  templateUrl: './person-appointment-editor-form.component.html',
  styleUrl: './person-appointment-editor-form.component.scss',
})
export class PersonAppointmentEditorFormComponent implements OnInit {
  @Input({ required: true }) person!: Person;
  @Input() appointments: ReferenceItem[] = [];
  @Input() communities: OrganizationUnitItem[] = [];
  @Input() ministryEvents: PersonEvent[] = [];
  @Input() assignmentEvents: PersonEvent[] = [];
  @Input() currentEvent: PersonEvent | null = null;
  @Input() appointmentEvents: PersonEvent[] = [];

  @Output() cancelled = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  readonly form;

  isSaving = false;

  get visibleAppointmentEvents(): PersonEvent[] {
    return this.appointmentEvents.slice().sort((a, b) => b.eventDate.localeCompare(a.eventDate));
  }

  formatDate(value: string): string {
    if (!value) return '-';
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
  }

  constructor(
    private readonly fb: FormBuilder,
    private readonly dateAdapter: DateAdapter<any>,
    private readonly personService: PersonService,
    private readonly notifications: NotificationService,
  ) {
    this.form = this.fb.group({
      validFrom: [null as any, Validators.required],
      validTo: [null as any],
      appointmentId: [null as number | null, Validators.required],
      organizationUnitId: [null as string | null],
      membershipCommunityId: [null as string | null],
      actPlaceMode: ['none' as ActPlaceMode],
      actOrganizationUnitId: [null as string | null],
      actLocation: ['', Validators.maxLength(200)],
    });
  }

  ngOnInit(): void {
    this.form.get('actPlaceMode')?.valueChanges
      .subscribe(() => this.syncActPlaceRules());

    if (this.currentEvent) {
      const actPlaceMode: ActPlaceMode =
        this.currentEvent.actOrganizationUnitId ? 'community' :
        this.currentEvent.location ? 'location' :
        'none';

      this.form.patchValue({
        validFrom: this.fromIsoDate(this.currentEvent.eventDate),
        validTo: this.fromIsoDate(this.currentEvent.endDate),
        appointmentId: this.currentEvent.appointmentId ?? null,
        organizationUnitId: this.currentEvent.organizationUnitId ?? null,
        actPlaceMode,
        actOrganizationUnitId: this.currentEvent.actOrganizationUnitId ?? null,
        actLocation: actPlaceMode === 'location' ? this.currentEvent.location ?? '' : '',
      });
      this.syncActPlaceRules();
      return;
    }

    this.form.patchValue({
      validFrom: this.dateAdapter.today(),
    });
    this.syncActPlaceRules();
  }

  get title(): string {
    return this.currentEvent ? 'Corregir encargo' : 'Nuevo encargo';
  }

  get description(): string {
    return this.currentEvent
      ? 'Corrige las fechas, el encargo o la unidad de este registro.'
      : 'Agrega un nuevo encargo para este miembro.';
  }

  get defaultUnitName(): string {
    return this.person.communityName || 'unidad de pertenencia';
  }

  get actPlaceMode(): ActPlaceMode {
    return this.form.get('actPlaceMode')?.value ?? 'none';
  }

  get isActPlaceVisible(): boolean {
    return this.actPlaceMode !== 'none';
  }

  get targetLevelId(): number | undefined {
    return this.selectedAppointment?.allowedOrganizationLevelIds?.[0];
  }

  private get selectedAppointment(): ReferenceItem | undefined {
    return this.appointments.find(option => option.id === this.form.get('appointmentId')?.value);
  }

  get membershipCommunityOptions(): OrganizationUnitItem[] {
    const validFrom = this.form.controls.validFrom.value;
    if (!validFrom) return [];

    const effectiveDate = this.toIsoDate(validFrom);
    const activeCommunityIds = new Set<string>();
    for (const activity of this.person.ministry?.activities ?? []) {
      if (activity.validFrom <= effectiveDate && (!activity.validTo || activity.validTo >= effectiveDate)) {
        activeCommunityIds.add(activity.unitId);
      }
    }
    for (const event of [...this.ministryEvents, ...this.appointmentEvents, ...this.assignmentEvents]) {
      if (event.organizationUnitId && event.eventDate <= effectiveDate && (!event.endDate || event.endDate >= effectiveDate)) {
        activeCommunityIds.add(event.organizationUnitId);
      }
    }

    const newActivityCommunityId = this.form.controls.organizationUnitId.value || this.person.communityId;
    if (newActivityCommunityId) activeCommunityIds.add(newActivityCommunityId);
    return this.communities.filter(community => activeCommunityIds.has(community.id));
  }

  get mustChooseMembershipCommunity(): boolean {
    return this.membershipCommunityOptions.length > 1;
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  onAddActPlace(): void {
    this.form.patchValue({ actPlaceMode: 'community' });
  }

  onRemoveActPlace(): void {
    this.form.patchValue({
      actPlaceMode: 'none',
      actOrganizationUnitId: null,
      actLocation: '',
    });
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.notifications.error('Completa los datos requeridos antes de guardar.');
      return;
    }

    if (this.mustChooseMembershipCommunity && !this.membershipCommunityOptions.some(
      community => community.id === this.form.controls.membershipCommunityId.value)) {
      this.notifications.error('Elegí dónde mantiene su pertenencia. Si seleccionás otra comunidad, se registrará el pase en la fecha efectiva.');
      return;
    }

    this.isSaving = true;
    const { validFrom, validTo, appointmentId, organizationUnitId, actPlaceMode, actOrganizationUnitId, actLocation, membershipCommunityId } = this.form.getRawValue();

    const request = {
      appointmentId: Number(appointmentId),
      unitId: organizationUnitId || undefined,
      actOrganizationUnitId: actPlaceMode === 'community' ? (actOrganizationUnitId || undefined) : undefined,
      actLocation: actPlaceMode === 'location' ? ((actLocation || '').trim() || undefined) : undefined,
      validFrom: this.toIsoDate(validFrom),
      validTo: validTo ? this.toIsoDate(validTo) : undefined,
      membershipCommunityId: this.mustChooseMembershipCommunity ? membershipCommunityId ?? undefined : undefined,
    };

    const operation$: import('rxjs').Observable<unknown> = this.currentEvent
      ? this.personService.updateAppointment(this.person.id, this.currentEvent.id, request)
      : this.personService.createAppointment(this.person.id, request);

    operation$.subscribe({
      next: () => {
        this.isSaving = false;
        this.notifications.success(this.currentEvent ? 'Encargo corregido correctamente.' : 'Encargo agregado correctamente.');
        this.saved.emit();
      },
      error: (err: unknown) => {
        this.isSaving = false;
        this.notifications.error(getPersonEventEditorErrorMessage(
          err,
          this.currentEvent ? 'No se pudo corregir el encargo.' : 'No se pudo guardar el encargo.',
        ));
      },
    });
  }

  private toIsoDate(value: any): string {
    const year = this.dateAdapter.getYear(value);
    const month = this.dateAdapter.getMonth(value) + 1;
    const day = this.dateAdapter.getDate(value);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  private fromIsoDate(value?: string | null): any | null {
    if (!value) {
      return null;
    }

    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) {
      return null;
    }

    return this.dateAdapter.createDate(year, month - 1, day);
  }

  private syncActPlaceRules(): void {
    const actOrganizationUnitIdControl = this.form.get('actOrganizationUnitId');
    const actLocationControl = this.form.get('actLocation');

    if (this.actPlaceMode === 'community') {
      actOrganizationUnitIdControl?.setValidators([Validators.required]);
      actLocationControl?.setValidators([Validators.maxLength(200)]);
      actLocationControl?.setValue('', { emitEvent: false });
    } else if (this.actPlaceMode === 'location') {
      actLocationControl?.setValidators([Validators.required, Validators.maxLength(200)]);
      actOrganizationUnitIdControl?.clearValidators();
      actOrganizationUnitIdControl?.setValue(null, { emitEvent: false });
    } else {
      actOrganizationUnitIdControl?.clearValidators();
      actLocationControl?.setValidators([Validators.maxLength(200)]);
      actOrganizationUnitIdControl?.setValue(null, { emitEvent: false });
      actLocationControl?.setValue('', { emitEvent: false });
    }

    actOrganizationUnitIdControl?.updateValueAndValidity({ emitEvent: false });
    actLocationControl?.updateValueAndValidity({ emitEvent: false });
  }
}
